import { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, AlertCircle, Lightbulb, FileText, Calendar, TrendingUp, Database, Trash2, FileDown, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { SupplierDriftBanner } from '../components/SupplierDriftBanner';
import { ContextualHelp } from '../components/ContextualHelp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProcessingResults = ({ token }) => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetails, setSessionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [undoingBatch, setUndoingBatch] = useState(false);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/spend/processing-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data.sessions);
      if (response.data.sessions.length > 0) {
        selectSession(response.data.sessions[0]);
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectSession = async (session) => {
    setSelectedSession(session);
    try {
      const response = await axios.get(`${API}/spend/processing-details/${session.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessionDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch session details:', error);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await axios.delete(`${API}/spend/session/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Upload session deleted successfully');
      
      // Refresh sessions list
      const updatedSessions = sessions.filter(s => s.id !== sessionId);
      setSessions(updatedSessions);
      
      // Clear selection if deleted session was selected
      if (selectedSession?.id === sessionId) {
        setSelectedSession(null);
        setSessionDetails(null);
        if (updatedSessions.length > 0) {
          selectSession(updatedSessions[0]);
        }
      }
      
      setDeleteConfirm(null);
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  const handleGenerateReport = async (uploadId) => {
    setGeneratingReport(true);
    try {
      const response = await axios.get(
        `${API}/spend/report/${uploadId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ProcureAide_Report_${uploadId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleUndoLastBatch = async (uploadId) => {
    setUndoingBatch(true);
    try {
      const response = await axios.delete(
        `${API}/spend/session/${uploadId}/undo-last-batch`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { batch_removed, records_removed, remaining_records } = response.data;
      toast.success(`Removed "${batch_removed.filename}" (${records_removed.toLocaleString()} records). ${remaining_records.toLocaleString()} records remaining.`);
      fetchSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to undo last upload');
    } finally {
      setUndoingBatch(false);
    }
  };

  const handleRetryClassify = async (uploadId) => {
    setRetrying(true);
    try {
      const tx = sessions.find(s => s.id === uploadId)?.category_mode === 'keep_existing' ? 'keep_existing' : 'custom';
      const res = await axios.post(`${API}/spend/classify/${uploadId}?taxonomy=${tx}`, {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 600000 });
      if (res.data.async) {
        toast.info(res.data.message, { duration: 5000 });
        // Poll for status
        const poll = setInterval(async () => {
          try {
            const status = await axios.get(`${API}/spend/classify-status/${uploadId}`,
              { headers: { Authorization: `Bearer ${token}` } });
            if (status.data.status === 'classified') {
              clearInterval(poll);
              toast.success('Classification complete!');
              setRetrying(false);
              fetchSessions();
              if (selectedSession?.id === uploadId) {
                const det = await axios.get(`${API}/spend/session/${uploadId}`,
                  { headers: { Authorization: `Bearer ${token}` } });
                setSessionDetails(det.data);
              }
            } else if (status.data.status === 'classification_failed') {
              clearInterval(poll);
              toast.error('Classification failed: ' + (status.data.error || 'Unknown error'));
              setRetrying(false);
            } else if (status.data.status === 'budget_exhausted') {
              clearInterval(poll);
              toast.error(status.data.error || 'LLM budget exhausted — please top up your Universal Key and retry.', { duration: 10000 });
              setRetrying(false);
              fetchSessions();
            }
          } catch (e) { /* continue */ }
        }, 3000);
      } else {
        toast.success(res.data.message || 'Classification complete!');
        setRetrying(false);
        fetchSessions();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Classification failed');
      setRetrying(false);
    }
  };

  const [retryingCleanse, setRetryingCleanse] = useState(false);
  const [vocabLocking, setVocabLocking] = useState(false);

  const handleVocabLockReclassify = async (uploadId) => {
    if (!window.confirm(
      'Reclassify all records with Vocabulary Lock?\n\n' +
      'This will rebuild a clean canonical taxonomy from your data, then re-classify every record using it. ' +
      'Synonym fragments (e.g. "MRO Spares" + "Spare Parts") will collapse into one consistent label. ' +
      'Existing classifications will be replaced. Run-time: ~1 min per 600 records.'
    )) return;
    setVocabLocking(true);
    try {
      const res = await axios.post(`${API}/spend/reclassify-vocab-lock/${uploadId}`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.info(res.data.message || 'Reclassification started in background', { duration: 6000 });
      const poll = setInterval(async () => {
        try {
          const status = await axios.get(`${API}/spend/classify-status/${uploadId}`,
            { headers: { Authorization: `Bearer ${token}` } });
          if (status.data.status === 'classified') {
            clearInterval(poll);
            toast.success('Vocabulary-Lock reclassification complete!');
            setVocabLocking(false);
            fetchSessions();
          } else if (status.data.status === 'classification_failed') {
            clearInterval(poll);
            toast.error('Reclassification failed: ' + (status.data.error || 'Unknown error'));
            setVocabLocking(false);
          } else if (status.data.status === 'budget_exhausted') {
            clearInterval(poll);
            toast.error(status.data.error || 'LLM budget exhausted — please top up your Universal Key and retry.', { duration: 10000 });
            setVocabLocking(false);
            fetchSessions();
          }
        } catch (e) { /* continue */ }
      }, 5000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reclassification failed');
      setVocabLocking(false);
    }
  };

  const handleRetryCleanse = async (uploadId) => {
    setRetryingCleanse(true);
    try {
      const res = await axios.post(`${API}/spend/cleanse/${uploadId}`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Cleansing complete — ${res.data.records_cleansed?.toLocaleString()} records processed, quality score: ${res.data.data_quality_score}%`);
      fetchSessions();
      if (selectedSession?.id === uploadId) {
        const det = await axios.get(`${API}/spend/session/${uploadId}`,
          { headers: { Authorization: `Bearer ${token}` } });
        setSessionDetails(det.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cleansing failed');
    } finally {
      setRetryingCleanse(false);
    }
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'classified': return 'bg-green-100 text-green-700';
      case 'cleansed': return 'bg-blue-100 text-blue-700';
      case 'classifying': return 'bg-amber-100 text-amber-700';
      case 'uploaded': return 'bg-yellow-100 text-yellow-700';
      case 'budget_exhausted': return 'bg-rose-100 text-rose-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'classified': return <CheckCircle className="text-green-600" size={20} />;
      case 'cleansed': return <TrendingUp className="text-blue-600" size={20} />;
      case 'classifying': return <Loader2 className="text-amber-600 animate-spin" size={20} />;
      default: return <Database className="text-gray-600" size={20} />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-500">Loading processing results...</div>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <FileText className="mx-auto mb-4 text-slate-300" size={48} />
          <p className="text-slate-600">No processing sessions yet. Upload data to get started.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ContextualHelp guideId="taxonomy" />
      <div>
        <h1 className="text-3xl font-heading font-bold text-[#0F172A] mb-2">Processing Results</h1>
        <p className="text-slate-600">View detailed results and insights from your data processing sessions</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sessions List - Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-sm sticky top-6">
            <div className="p-4 border-b border-slate-200 bg-slate-50">
              <h2 className="font-heading font-semibold text-slate-900">Sessions</h2>
              <p className="text-xs text-slate-500 mt-1">{sessions.length} total</p>
            </div>
            <div className="p-3 space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto" data-testid="processing-sessions-list">
              {sessions.map((session) => (
                <div key={session.id} className="relative">
                  <button
                    onClick={() => selectSession(session)}
                    className={`w-full text-left p-3 rounded-sm transition-all ${
                      selectedSession?.id === session.id
                        ? 'bg-[#2563EB] text-white shadow-md'
                        : 'bg-slate-50 hover:bg-slate-100 text-slate-900'
                    }`}
                    data-testid={`session-${session.id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-medium truncate pr-6">{session.filename}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs opacity-80">{session.total_records.toLocaleString()} records</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        selectedSession?.id === session.id
                          ? 'bg-white/20'
                          : session.status === 'classified' ? 'bg-green-100 text-green-700' :
                            session.status === 'cleansed' ? 'bg-blue-100 text-blue-700' :
                            session.status === 'classifying' ? 'bg-amber-100 text-amber-700' :
                            session.status === 'budget_exhausted' ? 'bg-rose-100 text-rose-700' :
                            'bg-slate-200 text-slate-700'
                      }`}>
                        {session.status === 'budget_exhausted' ? 'budget paused' : session.status}
                      </span>
                    </div>
                    <p className="text-xs opacity-70 mt-1">
                      {new Date(session.created_at).toLocaleDateString()}
                    </p>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirm(session.id);
                    }}
                    className="absolute top-2 right-2 p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                    data-testid={`delete-session-${session.id}`}
                    title="Delete session"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Session Details - Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {sessionDetails ? (
            <>
              {/* Header Card with Key Metrics */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-sm shadow-lg" data-testid="session-details">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h2 className="text-2xl font-heading font-bold mb-1">{selectedSession.filename}</h2>
                      <p className="text-blue-100 text-sm">
                        Processed on {new Date(selectedSession.created_at).toLocaleDateString()} at {new Date(selectedSession.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap justify-end">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        selectedSession.status === 'classified' ? 'bg-green-500' :
                        selectedSession.status === 'cleansed' ? 'bg-blue-500' :
                        selectedSession.status === 'classifying' ? 'bg-amber-500 animate-pulse' :
                        selectedSession.status === 'classification_failed' ? 'bg-red-500' :
                        selectedSession.status === 'budget_exhausted' ? 'bg-rose-500' :
                        'bg-slate-500'
                      }`} data-testid="session-status-badge">
                        {selectedSession.status === 'classifying' 
                          ? `Classifying... ${selectedSession.classification_progress || 0}/${selectedSession.classification_total || '?'}`
                          : selectedSession.status === 'budget_exhausted'
                          ? 'LLM Budget Exhausted'
                          : selectedSession.status}
                      </span>
                      {/* Retry Cleansing — for uploaded status */}
                      {selectedSession.status === 'uploaded' && (
                        <Button
                          onClick={() => handleRetryCleanse(selectedSession.id)}
                          disabled={retryingCleanse}
                          className="bg-teal-500 hover:bg-teal-600 text-white gap-1.5 font-semibold"
                          data-testid="retry-cleanse-button"
                        >
                          {retryingCleanse ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />}
                          {retryingCleanse ? 'Cleansing...' : 'Run Cleansing'}
                        </Button>
                      )}
                      {/* Classify / Retry Classification */}
                      {(selectedSession.status === 'cleansed' || selectedSession.status === 'classification_failed' || selectedSession.status === 'classifying' || selectedSession.status === 'budget_exhausted') && (
                        <Button
                          onClick={() => handleRetryClassify(selectedSession.id)}
                          disabled={retrying}
                          className="bg-amber-500 hover:bg-amber-600 text-white gap-1.5 font-semibold"
                          data-testid="retry-classify-button"
                        >
                          {retrying ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />}
                          {retrying ? 'Classifying...' :
                           selectedSession.status === 'classifying' ? 'Restart Classification' :
                           selectedSession.status === 'budget_exhausted' ? 'Resume Classification' : 'Classify'}
                        </Button>
                      )}
                      {/* Vocabulary-Lock reclassify — for already-classified sessions */}
                      {selectedSession.status === 'classified' && (
                        <Button
                          onClick={() => handleVocabLockReclassify(selectedSession.id)}
                          disabled={vocabLocking}
                          className="bg-purple-500 hover:bg-purple-600 text-white gap-1.5 font-semibold"
                          data-testid="vocab-lock-reclassify-button"
                          title="Rebuild a clean canonical taxonomy and re-classify all records"
                        >
                          {vocabLocking ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />}
                          {vocabLocking ? 'Reclassifying...' : 'Reclassify with Vocabulary Lock'}
                        </Button>
                      )}
                      <Button
                        onClick={() => handleGenerateReport(selectedSession.id)}
                        disabled={generatingReport}
                        className="bg-white text-blue-600 hover:bg-blue-50 gap-2 font-semibold"
                        data-testid="report-out-button"
                      >
                        {generatingReport ? <Loader2 size={18} className="animate-spin" /> : <FileDown size={18} />}
                        {generatingReport ? 'Generating...' : 'Report Out'}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white/10 backdrop-blur rounded-sm p-4">
                      <p className="text-blue-100 text-xs mb-1">Total Records</p>
                      <p className="text-3xl font-heading font-bold">{selectedSession.total_records.toLocaleString()}</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-sm p-4">
                      <p className="text-blue-100 text-xs mb-1">Data Quality</p>
                      <p className="text-3xl font-heading font-bold">
                        {sessionDetails.cleansing_result?.data_quality_score || 0}%
                      </p>
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-sm p-4">
                      <p className="text-blue-100 text-xs mb-1">AI Confidence</p>
                      <p className="text-3xl font-heading font-bold">
                        {sessionDetails.classification_result ? 
                          (sessionDetails.classification_result.average_confidence * 100).toFixed(0) : 0}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Budget Exhausted Banner */}
              {selectedSession.status === 'budget_exhausted' && (
                <div className="bg-rose-50 border-l-4 border-rose-500 rounded-sm p-4" data-testid="budget-exhausted-banner">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-rose-600 flex-shrink-0 mt-0.5" size={20} />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-rose-900">LLM budget exhausted — classification paused</p>
                      <p className="text-xs text-rose-800 mt-1">
                        {selectedSession.classification_error || 'Top up your Emergent Universal Key (Profile → Universal Key → Add Balance), then click "Resume Classification" above to continue where we left off.'}
                      </p>
                      {selectedSession.records_pending_classification > 0 && (
                        <p className="text-xs text-rose-700 mt-1 font-medium">
                          {selectedSession.records_pending_classification.toLocaleString()} records still to classify.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Pre-flight Supplier Drift Detector (only for classified sessions) */}
              {selectedSession.status === 'classified' && (
                <SupplierDriftBanner
                  token={token}
                  uploadId={selectedSession.id}
                  sessionStatus={selectedSession.status}
                />
              )}

              {selectedSession.batches?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="batch-history">
                  <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
                    <h3 className="text-sm font-semibold text-slate-800">Upload History</h3>
                    {selectedSession.batches.length > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUndoLastBatch(selectedSession.id)}
                        disabled={undoingBatch}
                        className="text-red-600 border-red-200 hover:bg-red-50 gap-1.5 text-xs h-8"
                        data-testid="undo-last-batch-btn"
                      >
                        {undoingBatch ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                        {undoingBatch ? 'Removing...' : 'Undo Last Upload'}
                      </Button>
                    )}
                  </div>
                  <div className="divide-y divide-slate-50">
                    {selectedSession.batches.map((batch, idx) => (
                      <div
                        key={batch.batch_id}
                        className={`flex items-center justify-between px-5 py-2.5 text-sm ${
                          idx === selectedSession.batches.length - 1 && selectedSession.batches.length > 1
                            ? 'bg-amber-50/50'
                            : ''
                        }`}
                        data-testid={`batch-row-${idx}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-slate-400 font-mono w-5">#{idx + 1}</span>
                          <span className="text-slate-700 font-medium">{batch.filename}</span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>{batch.records_count?.toLocaleString()} records</span>
                          <span>{new Date(batch.created_at).toLocaleDateString()} {new Date(batch.created_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</span>
                          {idx === selectedSession.batches.length - 1 && selectedSession.batches.length > 1 && (
                            <span className="text-amber-600 font-medium">Latest</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Results Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Cleansing Results */}
                {sessionDetails.cleansing_result && (
                  <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="cleansing-results">
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200 p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                          <TrendingUp className="text-white" size={20} />
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-slate-900">Data Cleansing</h3>
                          <p className="text-xs text-green-700">AI-powered normalization</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 space-y-3">
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm text-slate-600">Records Processed</span>
                        <span className="text-lg font-heading font-bold text-slate-900">
                          {sessionDetails.cleansing_result.records_cleansed?.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm text-slate-600">Quality Score</span>
                        <span className="text-lg font-heading font-bold text-green-600">
                          {sessionDetails.cleansing_result.data_quality_score}%
                        </span>
                      </div>
                      
                      {sessionDetails.cleansing_result.issues_found?.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-slate-100">
                          <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-2">
                            <AlertCircle size={14} className="text-orange-500" />
                            Issues Detected
                          </p>
                          <div className="space-y-2">
                            {sessionDetails.cleansing_result.issues_found.slice(0, 3).map((issue, idx) => (
                              <div key={idx} className="text-xs text-slate-600 pl-4 border-l-2 border-orange-300">
                                {typeof issue === 'string' ? issue : (issue.description || issue.type || JSON.stringify(issue))}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="mt-4 pt-3 border-t border-slate-100 bg-blue-50 -mx-4 -mb-4 px-4 py-3 rounded-b-sm" data-testid="cleansing-report-note">
                        <p className="text-xs text-blue-800">
                          <strong>Note:</strong> This is a preliminary analysis based on a data sample. For comprehensive AI-powered recommendations including data collection improvements, standardization suggestions, and best practices, visit the <strong>Reports</strong> page.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Classification Results */}
                {sessionDetails.classification_result && (
                  <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="classification-results">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200 p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                          <CheckCircle className="text-white" size={20} />
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-slate-900">Classification</h3>
                          <p className="text-xs text-blue-700">
                            {selectedSession.taxonomy_type === 'unspsc' ? 'UNSPSC Standard' : 'Custom Categories'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 space-y-3">
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm text-slate-600">Records Classified</span>
                        <span className="text-lg font-heading font-bold text-slate-900">
                          {sessionDetails.classification_result.records_classified?.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm text-slate-600">Avg Confidence</span>
                        <span className="text-lg font-heading font-bold text-blue-600">
                          {(sessionDetails.classification_result.average_confidence * 100).toFixed(1)}%
                        </span>
                      </div>
                      
                      {sessionDetails.classification_result.split_records_created > 0 && (
                        <div className="mt-2 p-3 bg-indigo-50 border border-indigo-200 rounded-sm">
                          <p className="text-xs font-semibold text-indigo-800 mb-1">Multi-Item Splitting</p>
                          <p className="text-xs text-indigo-700">
                            {sessionDetails.classification_result.records_that_were_split} transaction(s) contained multiple items and were split into {sessionDetails.classification_result.split_records_created} separate entries for more accurate classification.
                          </p>
                        </div>
                      )}
                      
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <div className="text-xs text-slate-600 space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span>High confidence (≥90%): Strong classification</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                            <span>Medium (70-89%): Review recommended</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            <span>Low (&lt;70%): Manual correction needed</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* AI Recommendations */}
              {sessionDetails.recommendations && (
                <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="recommendations">
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-b border-yellow-200 p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                        <Lightbulb className="text-white" size={20} />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-slate-900">AI Recommendations</h3>
                        <p className="text-xs text-yellow-700">Insights to improve data quality</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {sessionDetails.recommendations.data_collection_improvements?.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
                              <span className="text-blue-600 text-xs font-bold">1</span>
                            </div>
                            <h4 className="font-semibold text-slate-900 text-sm">Data Collection</h4>
                          </div>
                          <ul className="space-y-2">
                            {sessionDetails.recommendations.data_collection_improvements.map((item, idx) => (
                              <li key={idx} className="text-sm text-slate-600 flex items-start gap-2 pl-2">
                                <span className="text-blue-500 mt-0.5">→</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {sessionDetails.recommendations.standardization_suggestions?.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-6 h-6 bg-purple-100 rounded flex items-center justify-center">
                              <span className="text-purple-600 text-xs font-bold">2</span>
                            </div>
                            <h4 className="font-semibold text-slate-900 text-sm">Standardization</h4>
                          </div>
                          <ul className="space-y-2">
                            {sessionDetails.recommendations.standardization_suggestions.map((item, idx) => (
                              <li key={idx} className="text-sm text-slate-600 flex items-start gap-2 pl-2">
                                <span className="text-purple-500 mt-0.5">→</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {sessionDetails.recommendations.preliminary_classification_tips?.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-6 h-6 bg-green-100 rounded flex items-center justify-center">
                              <span className="text-green-600 text-xs font-bold">3</span>
                            </div>
                            <h4 className="font-semibold text-slate-900 text-sm">Classification Tips</h4>
                          </div>
                          <ul className="space-y-2">
                            {sessionDetails.recommendations.preliminary_classification_tips.map((item, idx) => (
                              <li key={idx} className="text-sm text-slate-600 flex items-start gap-2 pl-2">
                                <span className="text-green-500 mt-0.5">→</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {sessionDetails.recommendations.best_practices?.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-6 h-6 bg-orange-100 rounded flex items-center justify-center">
                              <span className="text-orange-600 text-xs font-bold">4</span>
                            </div>
                            <h4 className="font-semibold text-slate-900 text-sm">Best Practices</h4>
                          </div>
                          <ul className="space-y-2">
                            {sessionDetails.recommendations.best_practices.map((item, idx) => (
                              <li key={idx} className="text-sm text-slate-600 flex items-start gap-2 pl-2">
                                <span className="text-orange-500 mt-0.5">→</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm p-12 text-center">
              <Database className="mx-auto mb-4 text-slate-300" size={64} />
              <p className="text-slate-500 text-lg">Select a session to view details</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-sm p-6 max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-heading font-semibold text-[#0F172A] mb-2">Delete Upload Session?</h3>
            <p className="text-sm text-slate-600 mb-4">
              This will permanently delete the upload session and all related data including:
            </p>
            <ul className="text-sm text-slate-600 mb-6 space-y-1">
              <li>• All spend records</li>
              <li>• Cleansing results</li>
              <li>• Classification data</li>
              <li>• AI recommendations</li>
              <li>• User corrections</li>
            </ul>
            <div className="flex gap-3">
              <Button
                onClick={() => setDeleteConfirm(null)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDeleteSession(deleteConfirm)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-button"
              >
                Delete Permanently
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};